<template>
  <button class="margin-top-xs" :class="buyNow ? 'link-sm no-text-decoration' : 'button secondary expanded'" @click="handleMutate">
    {{ title }}
  </button>
</template>

<script setup>
import { unref, markRaw, defineAsyncComponent } from 'vue';
import { brand } from 'config';
import { useProduct } from '~/modules/product/composables/useProduct';
import useFeatureEligibility from '~/modules/featureControl/composables/useFeatureEligibility';
import { useEnvProps } from '~/composables/useEnvProps';
import { useBrand } from '~/composables/useBrand';
import { useSlideout } from '~/composables/utils/useSlideout';

const props = defineProps({
  /**
   * The payload to send to the add to registry endpoint.
   * @type {import('vue').Prop<AddToRegistryPayload>}
   */
  payload: {
    type: Object,
    required: true,
  },
  payloadValidator: {
    /**
     * @type {import('vue').PropType<AddToRegistryValidator>}
     */
    type: Function,
    default: () => null,
  },
});

const { hmacKey, atrXmacysApiKey, tagMngrEnv } = useEnvProps();
const { isMcom } = useBrand();
const { buyNow } = useFeatureEligibility();
const { productId, productData } = useProduct();

const unrefBuyNow = unref(buyNow);
const slideoutRegistryComponent = markRaw(
  defineAsyncComponent(
    () => import(/* webpackChunkName: "AddToRegistry" */ /* webpackPrefatch: true */ '@nuxt-common/add-to-registry/app/components/Registry.vue'),
  ),
);
// registry title
const title = isMcom && !unrefBuyNow ? 'Add To Registry' : 'Add to registry';

const slideoutOnSuccess = () => {
  const { showSlideout } = useSlideout();

  showSlideout({
    id: `add-to-registry-${productId}`,
    title: 'Add to Registry',
    component: slideoutRegistryComponent,
    props: {
      registryProperty: productData,
      tagMngrEnv,
      brand,
      hmac: hmacKey,
      macysXapiKey: atrXmacysApiKey,
      deviceType: 'SITE', // TODO update deviceType
      ...props.payload,
    },
  });
};

const emits = defineEmits(['error']);

const handleMutate = async () => {
  const { useAddToRegistry } = await import('../composables/useAddToRegistry');
  const { mutate } = useAddToRegistry(props.payload, {
    payloadValidator: props.payloadValidator,
    onSuccess: slideoutOnSuccess,
    emits,
  });
  try {
    await mutate();
  } catch (err) {
    console.log('handleMutate() in AddToRegistry.vue', err);
  }
};
</script>

<style lang="scss" scoped>
.no-text-decoration {
  text-transform: none;
}
</style>
